import React, { useEffect, useMemo, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input } from '@node-space/ui-components'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useBankDetailValidation } from 'pages/Contacts/hooks/useBankDetailsValidation'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { resetBankAddress } from 'pages/Contacts/utils/fiatInstruments'
import { BeneficiaryDetails, BeneficiaryDetailsCurrency, PaymentMethods } from 'types/beneficiaries'
import { AddressDetails } from './AddressDetails'

type IntermediaryBankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  contactBeneficiary?: BeneficiaryDetails
}

export const IntermediaryBankDetails = ({
  formValues,
  errors,
  clearErrors,
  register,
  setError,
  setValue,
  trigger,
  contactBeneficiary,
}: IntermediaryBankDetailsProps) => {
  const { t } = useTranslation()
  const { setRequestError } = useMappedErrorMessage()

  const [isRoutingNumberValid, setIsRoutingNumberValid] = useState<boolean>(false)
  const [isValidBic, setIsValidBic] = useState<boolean>(false)

  const { data: countries, isPending: countriesLoading } = useCountriesQuery(true)
  const {
    refetch: validateRoutingNumber,
    isFetching: isValidatingRoutingNumber,
    isError: isValidatingRoutingNumberError,
    data: routingNumberBankDetails,
  } = useRoutingNumberValidation(formValues?.intermediaryBankRoutingNumber, setRequestError, false)

  const {
    refetch: validateBic,
    isFetching: isValidatingBic,
    isError: isValidatingBicError,
    data: bicValidationData,
    error: bicValidationError,
  } = useBicValidation(formValues?.intermediaryBankCode, setRequestError, false)

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, clearErrors])

  useBankDetailValidation({
    formValues,
    setValue,
    setError,
    clearErrors,
    bicValidationProps: {
      isValidatingBicError,
      bicValidationData,
      bicValidationError,
      setIsValidBic,
      type: 'intermediaryBank',
    },
    contactBeneficiary,
  })

  useEffect(() => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!routingNumberBankDetails) return

    const { bankDetails, validationFailed } = routingNumberBankDetails
    const { bankAddress } = bankDetails ?? {}

    const isValid = !!bankAddress && !validationFailed?.length
    clearErrors()
    setIsRoutingNumberValid(isValid)

    if (!isValid) {
      setError('intermediaryBankRoutingNumber', { message: t('abaRoutingNumberError') })
      resetBankAddress(setValue, 'intermediaryBank')
    } else {
      const { addressLine1, addressLine2, city, region, postCode } = bankAddress ?? {}

      setValue('intermediaryBankAddressLine1', addressLine1)
      setValue('intermediaryBankAddressLine2', addressLine2)
      setValue('intermediaryBankCity', city)
      setValue('intermediaryBankRegion', region)
      setValue('intermediaryBankPostCode', postCode)
    }
  }, [routingNumberBankDetails, formValues?.currency, contactBeneficiary])

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isInternationalBankAccount = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isCountryUs = formValues?.intermediaryBankCountryCode === 'US'
  const isRoutingNumberVisible = isUsd && isInternationalBankAccount && isCountryUs

  const onIntermediaryFieldChange =
    (fieldName: keyof ContactFiatInstrumentFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(fieldName, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const intermediaryCountryOptions = useMemo(
    () =>
      countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      })) ?? [],
    [countries]
  )

  useEffect(() => {
    if (contactBeneficiary) return
    if (isValidatingRoutingNumberError && !formValues?.intermediaryBankRoutingNumber) {
      resetBankAddress(setValue, 'intermediaryBank')
    }
  }, [
    formValues?.intermediaryBankRoutingNumber,
    isValidatingRoutingNumberError,
    contactBeneficiary,
  ])

  useEffect(() => {
    if (contactBeneficiary) return
    if (isValidatingRoutingNumberError && formValues?.intermediaryBankRoutingNumber) {
      setError('intermediaryBankRoutingNumber', { message: t('abaRoutingNumberError') })
    }
  }, [
    isValidatingRoutingNumberError,
    formValues?.intermediaryBankRoutingNumber,
    contactBeneficiary,
  ])

  const onIntermediaryCountrySelection = (countryCode: string) => {
    setValue('intermediaryBankCountryCode', countryCode)
  }

  const handleIntermediaryBankCodeOnBlur = async () => {
    if (contactBeneficiary) return
    if (!formValues?.intermediaryBankCode) return
    clearErrors()
    validateBic()
  }

  const handleIntermediaryRoutingBlur = async () => {
    if (contactBeneficiary) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return
    if (!formValues?.intermediaryBankRoutingNumber) return

    const isValid = await trigger('intermediaryBankRoutingNumber')

    if (!isValid) return

    await validateRoutingNumber()
  }

  return (
    <Box width="full" paddingT={12} gap={12}>
      <Box paddingY={12}>
        <Input
          {...register('intermediaryBankName')}
          name="intermediaryBankName"
          testId="intermediaryBankName"
          label={t('manageBeneficiaries.intermediarysBankName')}
          placeholder={t('enterBankName')}
          errorText={errors?.intermediaryBankName?.message}
          error={!!errors?.intermediaryBankName?.message}
          onChange={onIntermediaryFieldChange('intermediaryBankName')}
        />
      </Box>

      <Box paddingB={12}>
        <Combobox
          {...register('intermediaryBankCountryCode')}
          key={formValues?.intermediaryBankCountryCode}
          label={t('country')}
          id={t('intermediaryBankCountryCode')}
          selectPlaceholder={t('searchOrSelectACountry')}
          items={intermediaryCountryOptions}
          onChange={onIntermediaryCountrySelection}
          loading={countriesLoading}
          defaultValue={formValues?.intermediaryBankCountryCode}
          errorText={errors?.intermediaryBankCountryCode?.message}
        />
      </Box>

      <Box paddingB={12} flexSize="auto" width="full">
        <Input
          {...register('intermediaryBankCode')}
          name="intermediaryBankCode"
          testId="intermediaryBankCode"
          label={t('formFields.bic')}
          placeholder={t('formFields.placeholder.bic')}
          errorText={errors?.intermediaryBankCode?.message}
          error={!!errors?.intermediaryBankCode?.message}
          onChange={onIntermediaryFieldChange('intermediaryBankCode')}
          onBlur={handleIntermediaryBankCodeOnBlur}
          loading={isValidatingBic}
          IconButton={
            isValidBic &&
            formValues?.intermediaryBankCode && (
              <Button variant="ghost" iconElement={<Icon name="Check" />} />
            )
          }
          iconPosition="right"
        />
      </Box>

      {isRoutingNumberVisible && (
        <Box paddingB={12}>
          <Input
            {...register('intermediaryBankRoutingNumber')}
            testId="intermediaryBankRoutingNumber"
            label={t('formFields.routingNumber')}
            placeholder={t('formFields.placeholder.routingNumber')}
            errorText={errors?.intermediaryBankRoutingNumber?.message}
            error={!!errors?.intermediaryBankRoutingNumber?.message}
            value={formValues?.intermediaryBankRoutingNumber}
            onChange={onIntermediaryFieldChange('intermediaryBankRoutingNumber')}
            loading={isValidatingRoutingNumber}
            onBlur={handleIntermediaryRoutingBlur}
            IconButton={
              isRoutingNumberValid &&
              formValues?.intermediaryBankRoutingNumber && (
                <Button variant="ghost" iconElement={<Icon name="Check" />} />
              )
            }
            iconPosition="right"
          />
        </Box>
      )}

      <AddressDetails
        formValues={formValues}
        errors={errors}
        type="intermediaryBank"
        clearErrors={clearErrors}
        register={register}
        setError={setError}
        setValue={setValue}
      />
    </Box>
  )
}
